<!-- adapted from https://github.com/pstanoev/simple-svelte-autocomplete -->
<script>
  import { slide } from "svelte/transition";

  import { titleCase } from "title-case";
  import Icon from "../Helpers/Icon.svelte";

  // the list of items  the user can select from
  export let items = [];
  export let icon = null;

  // function to use to get all items (alternative to providing items)
  export let searchFunction = false;

  // field of each item that's used for the labels in the list
  export let labelFieldName = undefined;
  export let keywordsFieldName = labelFieldName;
  export let valueFieldName = undefined;

  export let labelFunction = function (item) {
    if (item === undefined || item === null) {
      return "";
    }
    return labelFieldName ? item[labelFieldName] : item;
  };

  export let keywordsFunction = function (item) {
    if (item === undefined || item === null) {
      return "";
    }
    return keywordsFieldName ? item[keywordsFieldName] : labelFunction(item);
  };

  export let valueFunction = function (item) {
    if (item === undefined || item === null) {
      return item;
    }
    return valueFieldName ? item[valueFieldName] : item;
  };

  export let keywordsCleanFunction = function (keywords) {
    return keywords;
  };

  export let textCleanFunction = function (userEnteredText) {
    return userEnteredText;
  };

  export let beforeChange = function (oldSelectedItem, newSelectedItem) {
    return true;
  };
  export let onChange = function (newSelectedItem) {};

  // Behaviour properties
  export let selectFirstIfEmpty = false;
  export let minCharactersToSearch = 1;
  export let maxItemsToShowInList = 0;

  // delay to wait after a keypress to search for new items
  export let delay = 0;

  // true to perform local filtering of items, even if searchFunction is provided
  export let localFiltering = true;

  // UI properties
  // text displayed when no items match the input text
  export let noResultsText = "No results found";

  // the text displayed when no option is selected
  export let placeholder = undefined;

  // apply a className to the control
  export let className = undefined;

  // HTML input UI properties
  // apply a className to the input control
  export let inputClassName = undefined;
  // apply a id to the input control
  export let inputId = undefined;
  // generate an HTML input with this name, containing the current value
  export let name = undefined;
  // add the title to the HTML input
  export let title = undefined;

  // apply a className to the dropdown div
  export let dropdownClassName = undefined;

  // option to hide the dropdown arrow
  export let hideArrow = false;

  // option to show clear selection button
  export let showClear = false;

  // adds the disabled tag to the HTML input
  export let disabled = false;

  export let debug = false;

  // --- Public State ----
  // selected item state
  export let selectedItem = undefined;
  export let value = undefined;

  // --- Internal State ----
  const uniqueId = "sautocomplete-" + Math.floor(Math.random() * 1000);

  // HTML elements
  let input;
  let list;

  // UI state
  let opened = false;
  let highlightIndex = -1;
  let text;
  let filteredTextLength = 0;

  // view model
  let filteredListItems;
  let listItems = [];

  // other state
  let inputDelayTimeout;

  // -- Reactivity --
  function onSelectedItemChanged() {
    value = valueFunction(selectedItem);
    text = safeLabelFunction(selectedItem);
    onChange(selectedItem);
  }

  $: selectedItem, onSelectedItemChanged();

  $: showList =
    opened && ((items && items.length > 0) || filteredTextLength > 0);

  // --- Functions ---
  function safeStringFunction(theFunction, argument) {
    if (typeof theFunction !== "function") {
      console.error(
        "Not a function: " + theFunction + ", argument: " + argument
      );
    }
    let originalResult;
    try {
      originalResult = theFunction(argument);
    } catch (error) {
      console.warn(
        "Error executing Autocomplete function on value: " +
          argument +
          " function: " +
          theFunction
      );
    }
    let result = originalResult;
    if (result === undefined || result === null) {
      result = "";
    }
    if (typeof result !== "string") {
      result = result.toString();
    }
    return result;
  }

  function safeLabelFunction(item) {
    // console.log("labelFunction: " + labelFunction);
    // console.log("safeLabelFunction, item: " + item);
    return safeStringFunction(labelFunction, item);
  }

  function safeKeywordsFunction(item) {
    // console.log("safeKeywordsFunction");
    const keywords = safeStringFunction(keywordsFunction, item);
    let result = safeStringFunction(keywordsCleanFunction, keywords);
    result = result.toLowerCase().trim();
    if (debug) {
      console.log(
        "Extracted keywords: '" +
          result +
          "' from item: " +
          JSON.stringify(item)
      );
    }
    return result;
  }

  function prepareListItems() {
    let tStart;
    if (debug) {
      tStart = performance.now();
      console.log("prepare items to search");
      console.log("items: " + JSON.stringify(items));
    }

    if (!Array.isArray(items)) {
      console.warn(
        "Autocomplete items / search function did not return array but",
        items
      );
      items = [];
    }

    const length = items ? items.length : 0;
    listItems = new Array(length);

    if (length > 0) {
      items.forEach((item, i) => {
        const listItem = getListItem(item);
        if (listItem == undefined) {
          console.log("Undefined item for: ", item);
        }
        listItems[i] = listItem;
      });
    }

    if (debug) {
      const tEnd = performance.now();
      console.log(
        listItems.length +
          " items to search prepared in " +
          (tEnd - tStart) +
          " milliseconds"
      );
    }
  }

  function getListItem(item) {
    return {
      // keywords representation of the item
      keywords: safeKeywordsFunction(item),
      // item label
      label: safeLabelFunction(item),
      label2: `Part of ${item.system_name}`,
      //   label3visible: item.Occupation !== "N/A",
      label3visible: true,
      label3: item.Occupation,
      // store reference to the origial item
      item: item,
    };
  }

  $: items, prepareListItems();

  function prepareUserEnteredText(userEnteredText) {
    if (userEnteredText === undefined || userEnteredText === null) {
      return "";
    }

    const textFiltered = userEnteredText
      .replace(/[&/\\#,+()$~%.'":*?<>{}]/g, " ")
      .trim();

    filteredTextLength = textFiltered.length;

    if (minCharactersToSearch > 1) {
      if (filteredTextLength < minCharactersToSearch) {
        return "";
      }
    }

    const cleanUserEnteredText = textCleanFunction(textFiltered);
    const textFilteredLowerCase = cleanUserEnteredText.toLowerCase().trim();

    if (debug) {
      console.log(
        "Change user entered text '" +
          userEnteredText +
          "' into '" +
          textFilteredLowerCase +
          "'"
      );
    }
    return textFilteredLowerCase;
  }

  async function search() {
    let tStart;
    if (debug) {
      tStart = performance.now();
      console.log("Searching user entered text: '" + text + "'");
    }

    const textFiltered = prepareUserEnteredText(text);

    if (textFiltered === "") {
      filteredListItems = listItems;
      closeIfMinCharsToSearchReached();
      if (debug) {
        console.log(
          "User entered text is empty set the list of items to all items"
        );
      }
      return;
    }

    // external search which provides items
    if (searchFunction) {
      items = await searchFunction(textFiltered);
      prepareListItems();
    }

    // local search
    let tempfilteredListItems;
    if (localFiltering) {
      const searchWords = textFiltered.split(" ");

      tempfilteredListItems = listItems.filter((listItem) => {
        if (!listItem) {
          return false;
        }
        const itemKeywords = listItem.keywords;

        let matches = 0;
        searchWords.forEach((searchWord) => {
          if (itemKeywords.includes(searchWord)) {
            matches++;
          }
        });

        return matches >= searchWords.length;
      });
    } else {
      tempfilteredListItems = listItems;
    }

    const hlfilter = highlightFilter(textFiltered, ["label"]);
    const filteredListItemsHighlighted = tempfilteredListItems.map(hlfilter);

    filteredListItems = filteredListItemsHighlighted;
    closeIfMinCharsToSearchReached();
    if (debug) {
      const tEnd = performance.now();
      console.log(
        "Search took " +
          (tEnd - tStart) +
          " milliseconds, found " +
          filteredListItems.length +
          " items"
      );
    }
  }

  $: text, search();

  function selectListItem(listItem) {
    // text = listItem.item.equableclassid;
    // console.log(listItem.item.equableclassid);
    // console.log("here");
    if (debug) {
      console.log("selectListItem");
    }
    if ("undefined" === typeof listItem) {
      if (debug) {
        console.log(`listItem ${i} is undefined. Can not select.`);
      }
      return false;
    }
    const newSelectedItem = listItem.item;
    if (beforeChange(selectedItem, newSelectedItem)) {
      selectedItem = newSelectedItem;
    }
    return true;
  }

  function selectItem() {
    if (debug) {
      console.log("selectItem");
    }
    const listItem = filteredListItems[highlightIndex];
    if (selectListItem(listItem)) {
      close();
    }
  }

  function up() {
    if (debug) {
      console.log("up");
    }

    open();
    if (highlightIndex > 0) highlightIndex--;
    highlight();
  }

  function down() {
    if (debug) {
      console.log("down");
    }

    open();
    if (highlightIndex < filteredListItems.length - 1) highlightIndex++;
    highlight();
  }

  function highlight() {
    if (debug) {
      console.log("highlight");
    }

    const query = ".selected";
    if (debug) {
      console.log("Seaching DOM element: " + query + " in " + list);
    }
    const el = list && list.querySelector(query);
    if (el) {
      if (typeof el.scrollIntoViewIfNeeded === "function") {
        if (debug) {
          console.log("Scrolling selected item into view");
        }
        el.scrollIntoViewIfNeeded();
      } else {
        if (debug) {
          console.warn(
            "Could not scroll selected item into view, scrollIntoViewIfNeeded not supported"
          );
        }
      }
    } else {
      if (debug) {
        console.warn("Selected item not found to scroll into view");
      }
    }
  }

  function onListItemClick(listItem) {
    if (debug) {
      console.log("onListItemClick");
    }

    if (selectListItem(listItem)) {
      close();
    }
  }

  function onDocumentClick(e) {
    if (debug) {
      console.log("onDocumentClick: " + JSON.stringify(e.target));
    }
    if (e.target.closest("." + uniqueId)) {
      if (debug) {
        console.log("onDocumentClick inside");
      }
      // resetListToAllItemsAndOpen();
      highlight();
    } else {
      if (debug) {
        console.log("onDocumentClick outside");
      }
      close();
    }
  }

  function onKeyDown(e) {
    if (debug) {
      console.log("onKeyDown");
    }

    let key = e.key;
    if (key === "Tab" && e.shiftKey) key = "ShiftTab";
    const fnmap = {
      Tab: opened ? down.bind(this) : null,
      ShiftTab: opened ? up.bind(this) : null,
      ArrowDown: down.bind(this),
      ArrowUp: up.bind(this),
      Escape: onEsc.bind(this),
    };
    const fn = fnmap[key];
    if (typeof fn === "function") {
      e.preventDefault();
      fn(e);
    }
  }

  function onKeyPress(e) {
    if (debug) {
      console.log("onKeyPress");
    }

    if (e.key === "Enter") {
      e.preventDefault();
      selectItem();
    }
  }

  function onInput(e) {
    if (debug) {
      console.log("onInput");
    }

    text = e.target.value;

    if (inputDelayTimeout) {
      clearTimeout(inputDelayTimeout);
    }

    if (delay) {
      inputDelayTimeout = setTimeout(processInput, delay);
    } else {
      processInput();
    }
  }

  function processInput() {
    search();
    highlightIndex = 0;
    open();
  }

  function onInputClick() {
    if (debug) {
      console.log("onInputClick");
    }
    resetListToAllItemsAndOpen();
  }

  function onEsc(e) {
    if (debug) {
      console.log("onEsc");
    }

    //if (text) return clear();
    e.stopPropagation();
    if (opened) {
      input.focus();
      close();
    }
  }

  function onFocus() {
    if (debug) {
      console.log("onFocus");
    }

    resetListToAllItemsAndOpen();
  }

  function resetListToAllItemsAndOpen() {
    if (debug) {
      console.log("resetListToAllItemsAndOpen");
    }

    filteredListItems = listItems;

    open();

    // find selected item
    if (selectedItem) {
      if (debug) {
        console.log(
          "Searching currently selected item: " + JSON.stringify(selectedItem)
        );
      }
      for (let i = 0; i < listItems.length; i++) {
        const listItem = listItems[i];
        if ("undefined" === typeof listItem) {
          if (debug) {
            console.log(`listItem ${i} is undefined. Skipping.`);
          }
          continue;
        }
        if (debug) {
          console.log("Item " + i + ": " + JSON.stringify(listItem));
        }
        if (selectedItem == listItem.item) {
          highlightIndex = i;
          if (debug) {
            console.log(
              "Found selected item: " + i + ": " + JSON.stringify(listItem)
            );
          }
          highlight();
          break;
        }
      }
    }
  }

  function open() {
    if (debug) {
      console.log("open");
    }

    // check if the search text has more than the min chars required
    if (isMinCharsToSearchReached()) {
      return;
    }

    opened = true;
  }

  function close() {
    if (debug) {
      console.log("close");
    }
    opened = false;

    if (!text && selectFirstIfEmpty) {
      highlightFilter = 0;
      selectItem();
    }
  }

  function isMinCharsToSearchReached() {
    return (
      minCharactersToSearch > 1 && filteredTextLength < minCharactersToSearch
    );
  }

  function closeIfMinCharsToSearchReached() {
    if (isMinCharsToSearchReached()) {
      close();
    }
  }

  function clear() {
    if (debug) {
      console.log("clear");
    }

    text = "";
    selectedItem = undefined;

    setTimeout(() => {
      input.focus();
      close();
    });
  }

  function onBlur() {
    if (debug) {
      console.log("onBlur");
    }
    close();
  }
  // 'item number one'.replace(/(it)(.*)(nu)(.*)(one)/ig, '<b>$1</b>$2 <b>$3</b>$4 <b>$5</b>')
  function highlightFilter(q, fields) {
    const qs = "(" + q.trim().replace(/\s/g, ")(.*)(") + ")";
    const reg = new RegExp(qs, "ig");
    let n = 1;
    const len = qs.split(")(").length + 1;
    let repl = "";
    for (; n < len; n++) repl += n % 2 ? `<b>$${n}</b>` : `$${n}`;

    return (i) => {
      const newI = Object.assign({ highlighted: {} }, i);
      if (fields) {
        fields.forEach((f) => {
          if (!newI[f]) return;
          newI.highlighted[f] = newI[f].replace(reg, repl);
        });
      }
      return newI;
    };
  }
</script>

<div class="relative h-12 align-top w-full {uniqueId}">
  <input
    type="text"
    class="block w-full h-12 pr-10 text-base font-medium placeholder-gray-300 whitespace-pre-wrap transition bg-gray-100 border-none rounded-sm break-word md:text-lg lg:text-xl text-neutral"
    {placeholder}
    {name}
    {disabled}
    {title}
    bind:this={input}
    bind:value={text}
    on:input={onInput}
    on:focus={onFocus}
    on:keydown={onKeyDown}
    on:click={onInputClick}
    on:keypress={onKeyPress}
  />

  <div
    class="rounded-sm border border-neutral-2 bg-white relative px-0 overflow-y-auto top-1 max-h-96 select-none z-50 {showList
      ? ''
      : 'hidden'} w-full"
    bind:this={list}
  >
    {#if filteredListItems && filteredListItems.length > 0}
      {#each filteredListItems as listItem, i}
        {#if listItem}
          <div
            class="py-1 px-3.5 text-neutral cursor-pointer leading-none {i ===
            highlightIndex
              ? 'bg-neutral-5'
              : ''}"
            on:click={() => onListItemClick(listItem)}
            on:pointerenter={() => {
              highlightIndex = i;
            }}
          >
            <slot
              name="item"
              item={listItem.item}
              label={listItem.highlighted
                ? listItem.highlighted.label
                : listItem.label}
            >
              {#if listItem.highlighted}
                <div class="py-2 mb-0">
                  <h5 class="mb-2 font-normal">
                    {@html listItem.highlighted.label}
                  </h5>
                  <div
                    class="flex items-center mb-0.5 text-sm text-neutral-2 md:text-base space-x-1"
                  >
                    <Icon name="puzzle-piece-Light" size="20" fill="#6D6E71" />
                    <p class="ml-2">
                      <!-- <strong>Workers Covered: </strong> -->
                      {titleCase(listItem.item.plan_fullname)}
                    </p>
                  </div>
                  <div
                    class="flex items-center mb-0.5 text-sm text-neutral-2 md:text-base space-x-1"
                  >
                    <Icon name="users-Light" size="20" fill="#6D6E71" />
                    <p class="ml-2">
                      <!-- <strong>Workers Covered: </strong> -->
                      {titleCase(listItem.item.Occupation)}
                    </p>
                  </div>
                  <div
                    class="flex items-center mb-0.5 text-sm text-neutral-2 md:text-base space-x-1"
                  >
                    <Icon
                      name="clipboard-list-Light"
                      size="20"
                      fill="#6D6E71"
                    />
                    <p class="ml-2">
                      <!-- <strong>Plan Type: </strong> -->
                      {titleCase(listItem.item.plantype)}
                    </p>
                  </div>
                </div>
              {:else}
                <div class="py-2 mb-0">
                  <h5 class="mb-2 font-normal">
                    {@html listItem.label}
                  </h5>
                  <div
                    class="flex items-center mb-0.5 text-sm text-neutral-2 md:text-base space-x-1"
                  >
                    <Icon name="puzzle-piece-Light" size="20" fill="#6D6E71" />
                    <p class="ml-2">
                      <!-- <strong>Workers Covered: </strong> -->
                      {titleCase(listItem.item.plan_fullname)}
                    </p>
                  </div>
                  <div
                    class="flex items-center mb-0.5 text-sm text-neutral-2 md:text-base space-x-1"
                  >
                    <Icon name="users-Light" size="20" fill="#6D6E71" />
                    <p class="ml-2">
                      <!-- <strong>Workers Covered: </strong> -->
                      {titleCase(listItem.item.Occupation)}
                    </p>
                  </div>
                  <div
                    class="flex items-center mb-0.5 text-sm text-neutral-2 md:text-base space-x-1"
                  >
                    <Icon
                      name="clipboard-list-Light"
                      size="20"
                      fill="#6D6E71"
                    />
                    <p class="ml-2">
                      <!-- <strong>Plan Type: </strong> -->
                      {titleCase(listItem.item.plantype)}
                    </p>
                  </div>
                </div>
              {/if}
            </slot>
          </div>
        {/if}
      {/each}
    {:else if noResultsText}
      <div class="py-1.5 px-3.5 text-neutral-2 leading-none">
        <slot name="no-results" {noResultsText}>{noResultsText}</slot>
      </div>
    {/if}
  </div>

  {#if icon}
    {#if text.length > 0}
      <div
        class="absolute transition transform -translate-y-1/2 cursor-pointer top-1/2 right-2 hover:opacity-80"
        on:click={clear}
        transition:slide
      >
        <Icon name="times-Regular" fill="#082A30" size="24" stroke="#082A30" />
      </div>
    {:else}
      <div
        class="absolute transform -translate-y-1/2 top-1/2 right-2"
        transition:slide
      >
        <Icon name={icon} fill="#082A30" size="24" stroke="#082A30" />
      </div>
    {/if}
  {/if}
</div>

<svelte:window on:click={onDocumentClick} />

<style>
  .font-normal {
    font-weight: normal;
  }
</style>
